<template>
  <BaseModal
    :is-open="isOpen"
    title="Reconnection Request"
    max-width="550px"
    :closable="false"
  >
    <div class="reconnection-container">
      <!-- Player Info -->
      <div class="player-info">
        <div class="player-avatar">
          <img
            v-if="playerData?.profile_picture"
            :src="`/images/profiles/${playerData.profile_picture}.png`"
            :alt="playerData.player_name"
            class="avatar-image"
          />
          <div v-else class="avatar-initials">
            {{ getInitials(playerData?.player_name || '?') }}
          </div>
        </div>

        <div class="player-details">
          <h3 class="player-name">{{ playerData?.player_name }}</h3>
          <p class="reconnection-message">
            <span class="material-symbols-outlined">sync</span>
            <span>wants to reconnect to the game</span>
          </p>
          <div v-if="playerData?.seat !== undefined" class="seat-badge">
            <span class="material-symbols-outlined">event_seat</span>
            <span>Seat {{ playerData.seat + 1 }}</span>
            <span class="team-indicator">{{ getTeamLabel(playerData.seat) }}</span>
          </div>
        </div>
      </div>

      <!-- Disconnection Info -->
      <div class="disconnect-info">
        <div class="info-item">
          <span class="material-symbols-outlined">schedule</span>
          <span>Disconnected {{ formatDisconnectTime(playerData?.disconnect_time) }}</span>
        </div>
        <div v-if="playerData?.turns_missed" class="info-item warning">
          <span class="material-symbols-outlined">warning</span>
          <span>{{ playerData.turns_missed }} turn(s) missed</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="reconnection-actions">
        <button @click="handleDeny" class="btn-deny">
          <span class="material-symbols-outlined">block</span>
          <span>Deny</span>
        </button>
        <button @click="handleApprove" class="btn-approve">
          <span class="material-symbols-outlined">check_circle</span>
          <span>Allow Reconnection</span>
        </button>
      </div>

      <!-- Help Text -->
      <p class="help-text">
        Allowing reconnection will restore the player to their previous seat and hand.
      </p>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * ReconnectionModal Component
 *
 * Displays reconnection request from a previously disconnected player.
 * Game creator can approve or deny the reconnection.
 *
 * Props:
 * - player_name: Name of reconnecting player
 * - player_id: ID of player
 * - profile_picture: Player's avatar
 * - seat: Player's original seat (0-3)
 * - disconnect_time: When player disconnected (ISO timestamp)
 * - turns_missed: Number of turns missed while disconnected
 *
 * Emits:
 * - approve: { player_id: string } - Reconnection approved
 * - deny: { player_id: string } - Reconnection denied
 */

interface Props {
  isOpen: boolean
  data: {
    player_name?: string
    player_id?: string
    profile_picture?: string
    seat?: number
    disconnect_time?: string
    turns_missed?: number
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  approve: [{ player_id: string }]
  deny: [{ player_id: string }]
  close: []
}>()

/**
 * Player data
 */
const playerData = computed(() => props.data)

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Get team label for seat
 */
function getTeamLabel(seat: number): string {
  return seat % 2 === 0 ? 'Team A' : 'Team B'
}

/**
 * Format disconnect time (relative)
 */
function formatDisconnectTime(timestamp?: string): string {
  if (!timestamp) return 'recently'

  const disconnectTime = new Date(timestamp).getTime()
  const now = Date.now()
  const diff = now - disconnectTime

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  return `${seconds} second${seconds !== 1 ? 's' : ''} ago`
}

/**
 * Handle approve
 */
function handleApprove(): void {
  if (playerData.value?.player_id) {
    emit('approve', { player_id: playerData.value.player_id })
  }
}

/**
 * Handle deny
 */
function handleDeny(): void {
  if (playerData.value?.player_id) {
    emit('deny', { player_id: playerData.value.player_id })
  }
}
</script>

<style scoped>
.reconnection-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Player Info */
.player-info {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.player-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(232, 197, 71, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8c547, #d4af37);
  color: #0d1f2d;
  font-size: 1.75rem;
  font-weight: 700;
}

.player-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.player-name {
  font-size: 1.375rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.reconnection-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.reconnection-message .material-symbols-outlined {
  font-size: 1.25rem;
  color: #e8c547;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.seat-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  width: fit-content;
}

.seat-badge .material-symbols-outlined {
  font-size: 1rem;
}

.team-indicator {
  padding: 0.125rem 0.5rem;
  background: rgba(232, 197, 71, 0.2);
  border-radius: 4px;
  font-size: 0.75rem;
  color: #e8c547;
  margin-left: 0.25rem;
}

/* Disconnect Info */
.disconnect-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
}

.info-item .material-symbols-outlined {
  font-size: 1.125rem;
  color: rgba(232, 197, 71, 0.7);
}

.info-item.warning {
  color: rgba(251, 191, 36, 0.9);
}

.info-item.warning .material-symbols-outlined {
  color: #fbbf24;
}

/* Action Buttons */
.reconnection-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.btn-deny,
.btn-approve {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-deny {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-deny:hover {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
  transform: translateY(-2px);
}

.btn-approve {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.btn-approve:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-approve:active,
.btn-deny:active {
  transform: translateY(0);
}

.btn-deny .material-symbols-outlined,
.btn-approve .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Help Text */
.help-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin: 0;
  font-style: italic;
}

/* Responsive */
@media (max-width: 640px) {
  .player-info {
    flex-direction: column;
    text-align: center;
  }

  .player-details {
    align-items: center;
  }

  .seat-badge {
    margin: 0 auto;
  }

  .reconnection-actions {
    flex-direction: column;
  }
}
</style>
